<template>
	<section class="py-24">
		<MicsContainer v-if="filters">
		<header class="mb-12" id="project-flats">
			<h2 class="leading-tight text-4xl font-extrabold">{{$t('labels.flats_in')}} {{project.name}}</h2>
		</header>
		<ais-instant-search
		 index-name="listings"
		 :search-client="algolia"
		>
			<ais-hits
			  :class-names="{
			    'ais-Hits':
			      'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 min-[1650px]:grid-cols-4 gap-4 xl:gap-8',
			  }"
			>
		        <template v-slot="{ items, sendEvent }">
	        		<CardProperty v-for="hit in items" :key="hit.objectID" :listing="hit"/>
		    	</template>
            </ais-hits>
			<ais-configure
        :click-analytics.camel="false"
				:hits-per-page.camel="12"
				:filters="filters"
			></ais-configure>
            <nav>
              <ais-pagination @page-change="scrollToTop" class="flex justify-center items-center">
                <template
                  v-slot="{
                    currentRefinement,
                    nbPages,
                    pages,
                    isFirstPage,
                    isLastPage,
                    refine,
                    createURL,
                  }"
                >
                  <ul v-show="nbPages > 1" class="mt-5 md:mt-10 inline-flex -space-x-px text-sm">
                    <li v-if="!isFirstPage" hidden>
                      <a
                        :href="createURL(0)"
                        @click.prevent="refine(0)"
                        class="flex items-center justify-center px-3 h-8 ms-0 leading-tight text-gray-500 bg-white border border-e-0 border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="currentColor"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M5 1 1 5l4 4m6-8L7 5l4 4"
                          />
                        </svg>
                      </a>
                    </li>

                    <li v-if="!isFirstPage">
                      <a
                        :href="createURL(currentRefinement - 1)"
                        @click.prevent="refine(currentRefinement - 1)"
                        class="flex items-center justify-center px-3 h-8 ms-0 leading-tight text-gray-500 bg-white border border-e-0 border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="currentColor"
                        >
                          <path
                            d="M8.32137 12C8.0126 11.9999 7.71649 11.8736 7.49819 11.6488L2.8409 6.85472C2.62262 6.62997 2.5 6.32517 2.5 6.00736C2.5 5.68956 2.62262 5.38476 2.8409 5.16001L7.49819 0.365896C7.6056 0.251425 7.73407 0.160118 7.87613 0.0973047C8.01818 0.034491 8.17096 0.00142813 8.32556 4.52529e-05C8.48016 -0.00133763 8.63347 0.0289869 8.77657 0.0892499C8.91966 0.149513 9.04965 0.238507 9.15898 0.35104C9.2683 0.463573 9.35475 0.597391 9.4133 0.744686C9.47184 0.89198 9.5013 1.0498 9.49996 1.20894C9.49861 1.36808 9.46649 1.52535 9.40547 1.67158C9.34445 1.8178 9.25575 1.95005 9.14455 2.06061L5.31043 6.00736L9.14455 9.95411C9.30733 10.1217 9.41818 10.3353 9.46308 10.5677C9.50799 10.8002 9.48493 11.0411 9.39683 11.2601C9.30872 11.4791 9.15953 11.6662 8.9681 11.7979C8.77668 11.9296 8.55161 11.9999 8.32137 12Z"
                          />
                        </svg>
                      </a>
                    </li>
                    <li v-for="page in pages" :key="page" v-if="pages.length > 0">
                      <a
                        :href="createURL(page)"
                        @click.prevent="refine(page)"
                        class="flex items-center justify-center px-3 h-8 leading-tight border border-gray-300"
                        :class="{
                          'text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700 dark:border-gray-700 dark:bg-gray-700 dark:text-white':
                            page === currentRefinement,
                          'text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white':
                            page !== currentRefinement,
                        }"
                        >{{ page + 1 }}</a
                      >
                    </li>
                    <li v-if="!isLastPage">
                      <a
                        :href="createURL(currentRefinement + 1)"
                        @click.prevent="refine(currentRefinement + 1)"
                        class="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="currentColor"
                        >
                          <path
                            d="M3.67863 12C3.44839 11.9999 3.22332 11.9296 3.0319 11.7979C2.84047 11.6662 2.69128 11.4791 2.60317 11.2601C2.51507 11.0411 2.49201 10.8002 2.53692 10.5677C2.58182 10.3353 2.69267 10.1217 2.85546 9.95411L6.68957 6.00736L2.85546 2.06061C2.74425 1.95005 2.65555 1.8178 2.59453 1.67158C2.53351 1.52535 2.50139 1.36808 2.50004 1.20894C2.4987 1.0498 2.52816 0.89198 2.5867 0.744686C2.64525 0.597391 2.7317 0.463573 2.84102 0.35104C2.95034 0.238507 3.08034 0.149513 3.22343 0.0892499C3.36653 0.0289869 3.51984 -0.00133763 3.67444 4.52529e-05C3.82904 0.00142813 3.98182 0.034491 4.12387 0.0973047C4.26593 0.160118 4.3944 0.251425 4.50181 0.365896L9.1591 5.16001C9.37738 5.38476 9.5 5.68956 9.5 6.00736C9.5 6.32517 9.37738 6.62997 9.1591 6.85472L4.50181 11.6488C4.28351 11.8736 3.9874 11.9999 3.67863 12Z"
                          />
                        </svg>
                      </a>
                    </li>
                    <li v-if="!isLastPage" hidden>
                      <a
                        :href="createURL(nbPages)"
                        @click.prevent="refine(nbPages)"
                      >
                        ››
                      </a>
                    </li>
                  </ul>
                </template>
              </ais-pagination>
            </nav>
	    </ais-instant-search>
    </MicsContainer>
  </section>
</template>

<script setup lang="ts">
import {
  AisInstantSearch,
  AisHits,
  AisConfigure,
  AisPagination,
  AisStats,
} from "vue-instantsearch/vue3/es";

import {useWebsiteConfigurationStore} from '~/stores/websiteConfigurationStore';
const websiteConfigurationStore = useWebsiteConfigurationStore();
const {result, search} = useAlgoliaSearch('listings')
const algolia = useAlgoliaRef();
const filters = ref(null);

function scrollToTop() {
  document.getElementById("project-flats").scrollIntoView();
}

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  excludeSlug: {
    type: String,
    default: null,
  }
});

onMounted(async () => {
  let listingFilters = `language_code:${websiteConfigurationStore.configuration.languageCode} AND listing_id:${websiteConfigurationStore.configuration.listingId} AND announce_type: property-announces AND project.guid:${props.project.guid}`;
  if (props.excludeSlug) {
    listingFilters += ` AND NOT slug:"${props.excludeSlug}"`;
  }
  filters.value = listingFilters;
})
</script>